import React from "react";
import { Link } from "react-router-dom";
import { Users, Globe2, MapPin, Lock, ShieldCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { fileUrl } from "@/lib/api";
import { useReferences, labelFor } from "@/hooks/useReferences";

const initials = (name) => (name || "?").trim().split(/\s+/).slice(0, 2)
  .map((s) => s[0]).join("").toUpperCase();

export const MODE_LABEL = {
  open: "Anyone can join",
  approval: "Approval required",
  invite: "Invitation only",
};

const MODE_ICON = { open: Globe2, approval: ShieldCheck, invite: Lock };

/**
 * Group discovery card. Renders ONLY discovery-level data the API returned.
 *
 * Presentation follows the shared directory card (`pages/public/Directory.js`
 * `ResultCard`): borderless `Card` (GRP-DEC-295), the whole card is the navigation
 * target, and the identity image is the Group THUMBNAIL (`profile_image_url`) — never
 * the banner, which is a separate asset with a separate purpose (GRP-DEC-298).
 *
 * Whole-card navigation uses a stretched overlay link instead of wrapping the card in an
 * anchor, so nested interactive controls (Manage, Join / Request to join) keep their own
 * click target and never trigger card navigation. The overlay is a real `<Link>`, so
 * keyboard focus, Enter and middle-click/open-in-new-tab all behave normally.
 */
export function GroupCard({ group, footer, attention = false, attentionLabel = "" }) {
  const { references } = useReferences();
  const Icon = MODE_ICON[group.membership_mode] || Globe2;
  const place = [group.location?.city, group.location?.state].filter(Boolean).join(", ");
  const category = group.category ? String(group.category).replace(/-/g, " ") : "";
  // Industry labels come from the ONE globally cached reference bundle (`useReferences`),
  // so a directory page of 24 cards costs zero extra requests — no per-card lookup.
  const industries = group.industries || [];
  return (
    <Card className="group relative flex h-full flex-col gap-3 border-0 p-5"
          data-testid={`group-card-${group.id}`}>
      <Link to={group.url || "#"} aria-label={`View ${group.display_name}`}
            className="absolute inset-0 z-0 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            data-testid={`group-card-link-${group.id}`} />

      {/* Organizer attention dot. Nothing here reveals WHAT needs attention — the footer
          indicators and the Manage view do that — it just makes a card that needs a human
          impossible to miss in a grid. Organizer-only: the caller decides. */}
      {attention ? (
        <span className="absolute right-3 top-3 z-10 flex h-2.5 w-2.5" role="status"
              aria-label={attentionLabel || "Needs your attention"}
              title={attentionLabel || "Needs your attention"}
              data-testid={`group-card-attention-${group.id}`}>
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-500 opacity-60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-teal-600" />
        </span>
      ) : null}

      <div className="pointer-events-none relative z-10 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <Avatar className="h-12 w-12 shrink-0 rounded-xl">
            <AvatarImage src={group.profile_image_url ? fileUrl(group.profile_image_url) : undefined}
                         alt="" className="object-cover" />
            <AvatarFallback className="rounded-xl bg-teal-50 text-teal-700">
              {initials(group.display_name)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-[640] text-foreground transition-colors group-hover:text-primary">
              {group.display_name}
            </p>
            <p className="truncate text-sm text-primary">@{group.handle}</p>
          </div>
        </div>

        {group.short_description ? (
          <p className="line-clamp-2 text-sm text-foreground/80">{group.short_description}</p>
        ) : null}

        {/* Row 1 — Industry · Category · Online (GRP-DEC-297/327). Industry is capped at
            two chips plus a `+N`, the same cap the Group page's metadata row uses, so a
            group with ten industries never swamps the card. */}
        {industries.length ? (
          <div className="flex flex-wrap items-center gap-1.5"
               data-testid={`group-card-industries-${group.id}`}>
            {industries.slice(0, 2).map((slug) => (
              <Badge key={slug} variant="secondary" className="capitalize">
                {labelFor(references.industries, slug)}
              </Badge>
            ))}
            {industries.length > 2 ? (
              <Badge variant="outline"
                     data-testid={`group-card-industries-more-${group.id}`}>
                +{industries.length - 2}
              </Badge>
            ) : null}
          </div>
        ) : null}

        {/* Row 2 — Category · Online. Online and Location can coexist, so they are never
            crowded onto the same line. */}
        {category || group.is_online || group.is_local ? (
          <div className="flex flex-wrap items-center gap-1.5"
               data-testid={`group-card-classification-${group.id}`}>
            {category ? <Badge variant="secondary" className="capitalize">{category}</Badge> : null}
            {group.is_online ? (
              <span className="inline-flex items-center gap-1 text-sm text-muted-foreground"
                    data-testid={`group-card-online-${group.id}`}>
                <Globe2 className="h-3.5 w-3.5" />Online
              </span>
            ) : null}
            {group.is_local ? <Badge variant="outline">Local</Badge> : null}
          </div>
        ) : null}

        {/* Row 3 — Location, on its own line. */}
        {place ? (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground"
               data-testid={`group-card-location-${group.id}`}>
            <MapPin className="h-3.5 w-3.5 shrink-0" />{place}
          </div>
        ) : null}
      </div>

      <div className="pointer-events-none relative z-10 mt-auto flex items-center justify-between pt-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1" data-testid={`group-card-count-${group.id}`}>
          <Users className="h-3.5 w-3.5" />{group.member_count} {group.member_count === 1 ? "member" : "members"}
        </span>
        <span className="inline-flex items-center gap-1">
          <Icon className="h-3.5 w-3.5" />{MODE_LABEL[group.membership_mode] || ""}
        </span>
      </div>

      {footer ? (
        <div className="pointer-events-auto relative z-10"
             onClick={(e) => e.stopPropagation()}
             data-testid={`group-card-actions-${group.id}`}>
          {footer}
        </div>
      ) : null}
    </Card>
  );
}
